// Definició dels nivells de permisos
import menusConfig from "./Menus";

export const NIVELLS_PERMISOS = {
  BASIC: 1,
  EDITOR: 2,
  SUPER: 3,
  ADMIN: 4,
};

// Definim els permisos "generals"
const permisosGenerals = {
  ESBORRAR: [NIVELLS_PERMISOS.ADMIN],
  MODIFICAR: [NIVELLS_PERMISOS.EDITOR, NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN],
  CONSULTAR: [NIVELLS_PERMISOS.BASIC, NIVELLS_PERMISOS.EDITOR, NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN],
  CREAR: [NIVELLS_PERMISOS.EDITOR, NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN],
  ACCES: [NIVELLS_PERMISOS.ADMIN],
};

// Definim "excepcions" per a un àmbit concret (Permisos.txt).
// ESBORRAR general = només ADMIN. No reobrir-lo per entitats.
const permisosEspecifics = {
  "CREAR_USUARIS": [NIVELLS_PERMISOS.ADMIN],
  "MODIFICAR_USUARIS": [NIVELLS_PERMISOS.ADMIN],
  "ESBORRAR_USUARIS": [NIVELLS_PERMISOS.ADMIN],
  // ACCES general només permet ADMIN; Gestió laboral l'ha de permetre també a SUPER.
  "ACCES_GESTIO_LABORAL": [NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN],
  // La Gestió laboral d'Empleats (pestanya "Gestió" i vista global "Gestió
  // laboral -> Empleats") segueix el mateix criteri que Gestió laboral.
  "ACCES_EMPLEATS": [NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN],
};

// Funció per comprovar si pots gestionar un usuari segons la jerarquia
export function potGestionarUsuari(nivellGestor, esSuperusuariGestor, nivellObjectiu, esSuperusuariObjectiu, accio) {
  /*
    Regles jeràrquiques:
    - Pots gestionar (crear/modificar/consultar) usuaris del teu mateix
      nivell o inferior; MAI d'un nivell superior al teu.
    - EDITOR (2) no pot esborrar cap usuari (ni tan sols del seu nivell).
    - SUPERUSUARIS: poden gestionar qualsevol usuari, incloent altres ADMIN.
    - NO pots crear/modificar/esborrar un superusuari si tu no ets superusuari,
      encara que el nivell de Rol coincideixi.
  */

  // Protecció: no pots tocar un superusuari si tu no ets superusuari
  if (esSuperusuariObjectiu && !esSuperusuariGestor) {
    return false;
  }

  // Superusuaris poden fer tot amb qualsevol usuari
  if (esSuperusuariGestor) {
    return true;
  }

  // EDITOR (2) no pot esborrar ningú, ni tan sols del seu propi nivell
  if (accio === "ESBORRAR" && nivellGestor === NIVELLS_PERMISOS.EDITOR) {
    return false;
  }

  // Pots gestionar usuaris del teu mateix nivell o inferior; mai un de superior
  return nivellObjectiu <= nivellGestor;
}

// Funció per obtenir els rols que un usuari pot assignar
export function getRolsAssignables(nivellGestor, esSuperusuari, totsElsRols) {
  /*
    totsElsRols = [{idRol, Nom, NivellPermisos}, ...]

    Si esSuperusuari = true → pot assignar qualsevol rol.
    Si esSuperusuari = false → pot assignar rols del seu mateix nivell o inferior.
    (Convertir algú en superusuari és un flag apart, validat on s'assigna.)
  */

  if (esSuperusuari) {
    return totsElsRols;
  }

  return totsElsRols.filter(r => r.NivellPermisos <= nivellGestor);
}

// Funció per comprovar si pots crear un superusuari
export function potCrearSuperusuari(esSuperusuari) {
  // Només superusuaris poden crear altres superusuaris
  return esSuperusuari;
}

// La funció tincPermis original (per altres entitats)
export function tincPermis(nivellUsuari, accio, entitat = null) {
  const nivell = Number(nivellUsuari);
  const clauEspecifica = entitat ? `${accio}_${entitat}` : accio;

  // Busquem la clau a permisosEspecifics
  if (permisosEspecifics[clauEspecifica]) {
    return permisosEspecifics[clauEspecifica].includes(nivell);
  }

  // Si no hi ha excepció definida, usem la definició general
  const rolsGenerals = permisosGenerals[accio];
  if (!rolsGenerals) {
    return false;
  }

  return rolsGenerals.includes(nivell);
}

// Coincideix amb Menus.js (Agenda: EDITOR/SUPER/ADMIN) i AgendaAuth.php.
export function potAccedirAgenda(nivellUsuari) {
  return [NIVELLS_PERMISOS.EDITOR, NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN].includes(
    Number(nivellUsuari)
  );
}

export function rutaIniciSessio({ rol, esSuperUsuari, empresa }) {
  if (esSuperUsuari && !empresa) {
    return "/dashboard/empresa";
  }
  if (potAccedirAgenda(rol)) {
    return "/dashboard/agenda";
  }
  return "/dashboard";
}

function normalitzaPathDashboard(pathname) {
  const path = String(pathname || "").split("?")[0];
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

function pathCoincideixRuta(path, ruta) {
  if (!ruta) return false;
  return path === ruta || path.startsWith(`${ruta}/`);
}

const RUTES_EXTRES = {
  "/dashboard/contactes": [NIVELLS_PERMISOS.EDITOR, NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN],
  "/dashboard/gestioempleat": [NIVELLS_PERMISOS.SUPER, NIVELLS_PERMISOS.ADMIN],
};

export function rolsDeRutaDashboard(pathname) {
  const path = normalitzaPathDashboard(pathname);
  if (path === "/dashboard") {
    return [
      NIVELLS_PERMISOS.BASIC,
      NIVELLS_PERMISOS.EDITOR,
      NIVELLS_PERMISOS.SUPER,
      NIVELLS_PERMISOS.ADMIN,
    ];
  }

  let millor = null;
  function considera(ruta, roles) {
    if (!ruta || !roles || !pathCoincideixRuta(path, ruta)) return;
    if (!millor || ruta.length > millor.ruta.length) {
      millor = { ruta, roles };
    }
  }

  for (const [ruta, roles] of Object.entries(RUTES_EXTRES)) {
    considera(ruta, roles);
  }
  for (const menu of menusConfig) {
    considera(menu.path, menu.roles);
    for (const fill of menu.children || []) {
      considera(fill.path, fill.roles);
    }
  }
  return millor ? millor.roles : null;
}

export function potAccedirRutaDashboard(pathname, rol, { esSuperUsuari } = {}) {
  const path = normalitzaPathDashboard(pathname);
  if (path === "/dashboard/empresa" && esSuperUsuari) return true;
  const rols = rolsDeRutaDashboard(pathname);
  if (!rols) return true;
  return rols.includes(Number(rol));
}